/**
 * Apertum Global DID eligibility (did-verification skill).
 * getTokenId is only called after balanceOf > 0.
 *
 * is-new-flow lives on dex.apertum.io and has no Access-Control-Allow-Origin,
 * so the browser must use the same-origin proxy (never the dex host).
 */

export const DID_NFT_ADDRESS = '0xDE72695e54Bb44bEB1844C35CD3eA50f4F785F2D';

/** Upstream (server-side / Netlify rewrite only — never fetch from the browser). */
export const NEW_FLOW_KYC_API = 'https://dex.apertum.io/api/kyc/is-new-flow';

/** Browser same-origin path. Dev: Vite proxy. Prod: Netlify _redirects. */
export const NEW_FLOW_KYC_PROXY = '/api/did-new-flow-kyc';

/** Where applicants mint / verify a Global DID. */
export const KYC_APERTUM_URL = 'https://kyc.apertum.io';

export const DID_ABI = [
  'function exists(uint256 tokenId_) view returns (bool)',
  'function isKycValid(uint256 tokenId_) view returns (bool)',
  'function getKycExpiry(uint256 tokenId_) view returns (uint256)',
  'function getTokenId(address owner) view returns (uint256)',
  'function ownerOf(uint256 tokenId) view returns (address)',
  'function balanceOf(address owner) view returns (uint256)',
];

export type DidGateReason =
  | 'ok'
  | 'admin_bypass'
  | 'missing_did'
  | 'not_new_flow'
  | 'country_blocked'
  | 'kyc_invalid_or_expired'
  | 'did_not_exists'
  | 'api_error'
  | 'rpc_error';

export function needsDidOnboarding(reason?: DidGateReason | null): boolean {
  return (
    reason === 'missing_did' ||
    reason === 'not_new_flow' ||
    reason === 'kyc_invalid_or_expired' ||
    reason === 'did_not_exists'
  );
}

export function applicationSubmitError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return 'Failed to submit application. Please try again.';
}

export function submitErrorNeedsDidOnboarding(message: string): boolean {
  return /kyc\.apertum|Global DID|DID KYC|Apertum DID/i.test(message);
}

export interface DidEligibility {
  eligible: boolean;
  didId: number | null;
  isNewFlowKyc: boolean | null;
  isCountryBlocked: boolean | null;
  exists: boolean | null;
  isKycValid: boolean | null;
  kycExpiry: number | null;
  reason: DidGateReason;
  message: string;
}

/** Application forms stay locked until Global DID KYC is valid (admins bypass). */
export function canUseApplicationForm(
  eligibility: DidEligibility | null | undefined
): boolean {
  return Boolean(eligibility?.eligible);
}

export interface DidKycState {
  exists: boolean;
  isKycValid: boolean;
  kycExpiry: number;
}

export interface DidReader {
  balanceOf(wallet: string): Promise<{ isZero(): boolean } | number | bigint>;
  getTokenId(wallet: string): Promise<{ toNumber(): number } | number | bigint>;
  exists(didId: number): Promise<boolean>;
  isKycValid(didId: number): Promise<boolean>;
  getKycExpiry(didId: number): Promise<{ toNumber(): number } | number | bigint>;
  /** Optional batched KYC read (Multicall3). */
  readKycState?(didId: number): Promise<DidKycState>;
}

export type NewFlowFetcher = (didId: number) => Promise<{
  isNewFlowKyc: boolean;
  isCountryBlocked: boolean;
}>;

function toNumber(value: { toNumber(): number } | number | bigint): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') return Number(value);
  return value.toNumber();
}

function isZero(value: { isZero(): boolean } | number | bigint): boolean {
  if (typeof value === 'number') return value === 0;
  if (typeof value === 'bigint') return value === 0n;
  return value.isZero();
}

export async function resolveDidId(
  wallet: string,
  reader: DidReader
): Promise<number | null> {
  const bal = await reader.balanceOf(wallet);
  if (isZero(bal)) return null;
  const id = await reader.getTokenId(wallet);
  const n = toNumber(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export async function checkNewFlowKyc(
  didId: number,
  fetchImpl: typeof fetch = fetch
): Promise<{ isNewFlowKyc: boolean; isCountryBlocked: boolean }> {
  const bases = newFlowBases();
  const errors: string[] = [];
  for (const base of bases) {
    try {
      const res = await fetchImpl(
        `${base}?did=${encodeURIComponent(String(didId))}`,
        { method: 'GET', headers: { Accept: 'application/json' } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as {
        isNewFlowKyc?: unknown;
        isCountryBlocked?: unknown;
      };
      return {
        isNewFlowKyc: Boolean(data.isNewFlowKyc),
        isCountryBlocked: Boolean(data.isCountryBlocked),
      };
    } catch (err) {
      errors.push(err instanceof Error ? err.message : String(err));
    }
  }
  throw new Error(`is-new-flow failed: ${errors.join(' | ')}`);
}

function newFlowBases(): string[] {
  const override =
    typeof import.meta !== 'undefined'
      ? import.meta.env.VITE_DID_NEW_FLOW_API
      : undefined;
  if (override) return [override];
  // Same-origin only. dex.apertum.io does not send CORS headers, so a
  // production fallback to NEW_FLOW_KYC_API fails in the browser.
  return [NEW_FLOW_KYC_PROXY];
}

export async function checkOnChainKyc(
  didId: number,
  reader: DidReader
): Promise<DidKycState> {
  if (reader.readKycState) {
    return reader.readKycState(didId);
  }
  const [exists, isKycValid, expiry] = await Promise.all([
    reader.exists(didId),
    reader.isKycValid(didId),
    reader.getKycExpiry(didId),
  ]);
  return {
    exists: Boolean(exists),
    isKycValid: Boolean(isKycValid),
    kycExpiry: toNumber(expiry),
  };
}

/** Assigned admin: no APTMDAO NFT and no Global Standard DID required. */
export function adminBypassEligibility(): DidEligibility {
  return {
    eligible: true,
    didId: null,
    isNewFlowKyc: null,
    isCountryBlocked: null,
    exists: null,
    isKycValid: null,
    kycExpiry: null,
    reason: 'admin_bypass',
    message:
      'Assigned admin wallet — APTMDAO NFT and Global Standard DID are not required to use Admin.',
  };
}

export async function verifyGlobalDidAccess(opts: {
  wallet?: string;
  didId?: number;
  reader: DidReader;
  checkNewFlow?: NewFlowFetcher;
  enforceCountryBlock?: boolean;
}): Promise<DidEligibility> {
  const enforceCountry = opts.enforceCountryBlock !== false;
  const checkNewFlow = opts.checkNewFlow ?? ((id) => checkNewFlowKyc(id));

  let didId = opts.didId != null && opts.didId > 0 ? opts.didId : null;
  if (didId == null && opts.wallet) {
    try {
      didId = await resolveDidId(opts.wallet, opts.reader);
    } catch {
      return deny(null, 'rpc_error', 'Could not resolve DID for wallet (RPC error).');
    }
  }
  if (didId == null) {
    return deny(
      null,
      'missing_did',
      'This wallet has no Apertum Global DID. Get one at kyc.apertum.io, then return to apply.'
    );
  }

  let flow: { isNewFlowKyc: boolean; isCountryBlocked: boolean };
  try {
    flow = await checkNewFlow(didId);
  } catch {
    return deny(didId, 'api_error', 'We could not verify your DID right now. Try again shortly.');
  }

  if (!flow.isNewFlowKyc) {
    return {
      eligible: false,
      didId,
      isNewFlowKyc: false,
      isCountryBlocked: flow.isCountryBlocked,
      exists: null,
      isKycValid: null,
      kycExpiry: null,
      reason: 'not_new_flow',
      message:
        'Your DID is not on the Global DID standard yet. Complete verification at kyc.apertum.io, then return to apply.',
    };
  }

  if (enforceCountry && flow.isCountryBlocked) {
    return {
      eligible: false,
      didId,
      isNewFlowKyc: true,
      isCountryBlocked: true,
      exists: null,
      isKycValid: null,
      kycExpiry: null,
      reason: 'country_blocked',
      message: 'This DID is not eligible for this service in the current region.',
    };
  }

  let chain: { exists: boolean; isKycValid: boolean; kycExpiry: number };
  try {
    chain = await checkOnChainKyc(didId, opts.reader);
  } catch {
    return deny(didId, 'rpc_error', 'Could not read DID KYC on-chain (RPC error).');
  }

  if (!chain.exists) {
    return {
      eligible: false,
      didId,
      isNewFlowKyc: true,
      isCountryBlocked: flow.isCountryBlocked,
      exists: false,
      isKycValid: chain.isKycValid,
      kycExpiry: chain.kycExpiry,
      reason: 'did_not_exists',
      message: 'That DID id is not a valid token.',
    };
  }

  if (!chain.isKycValid) {
    return {
      eligible: false,
      didId,
      isNewFlowKyc: true,
      isCountryBlocked: flow.isCountryBlocked,
      exists: true,
      isKycValid: false,
      kycExpiry: chain.kycExpiry,
      reason: 'kyc_invalid_or_expired',
      message:
        'Your DID KYC has expired or is invalid. Renew it at kyc.apertum.io, then return to apply.',
    };
  }

  return {
    eligible: true,
    didId,
    isNewFlowKyc: true,
    isCountryBlocked: flow.isCountryBlocked,
    exists: true,
    isKycValid: true,
    kycExpiry: chain.kycExpiry,
    reason: 'ok',
    message: 'DID meets Global DID standard and on-chain KYC is valid.',
  };
}

function deny(
  didId: number | null,
  reason: DidGateReason,
  message: string
): DidEligibility {
  return {
    eligible: false,
    didId,
    isNewFlowKyc: null,
    isCountryBlocked: null,
    exists: null,
    isKycValid: null,
    kycExpiry: null,
    reason,
    message,
  };
}
